import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Spinner from '@cloudscape-design/components/spinner';
import { OpenInPostHogButton } from '@/components/OpenInPostHogButton';
import { posthogRetentionUrl } from '@/lib/posthogLinks';
import { CohortHeatmap } from './CohortHeatmap';
import { useCohorts } from './useCohorts';
import { serverDate } from '@etiquetachef/ui-shared/online';

const DEFAULT_LOOKBACK_MONTHS = 12;

function defaultRange(now: Date = serverDate()): { from: string; to: string } {
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const start = new Date(
    Date.UTC(end.getUTCFullYear(), end.getUTCMonth() - DEFAULT_LOOKBACK_MONTHS + 1, 1),
  );
  const fmt = (d: Date): string =>
    `${String(d.getUTCFullYear())}${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  return { from: fmt(start), to: fmt(end) };
}

/**
 * Tela `/dashboard/cohorts`: heatmap de retenção por coorte mensal dos
 * últimos 12 meses, com atalho para a mesma análise no PostHog.
 *
 * Origem: sem issue — commit 37823e3cb, 2026-05-04 (Sprint 4.10, ver
 * `docs/sprint-4.10-cohorts-funnel-churn.md`).
 *
 * Contrato: sem props e sem controle de período — a janela é fixa
 * (`DEFAULT_LOOKBACK_MONTHS`, calculada em UTC uma vez por montagem). Estados
 * LOADING/ERROR/READY vêm de `useCohorts`.
 *
 * Usado por: `routes/router.tsx` (rota `dashboard/cohorts`, só super-admin).
 *
 * Arquitetura: só apresentação; a leitura de `aggregations/cohorts/months` é
 * do `useCohorts`. O botão do PostHog só abre link externo, não lê nada.
 */
export function CohortsDashboardPage(): React.JSX.Element {
  const { t } = useTranslation();
  const range = useMemo(() => defaultRange(), []);
  const state = useCohorts(range.from, range.to);

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t('cohorts.subtitle')}
          actions={
            <OpenInPostHogButton
              href={posthogRetentionUrl()}
              surface="cohorts_retention"
            />
          }
        >
          {t('cohorts.title')}
        </Header>
      }
    >
      {state.kind === 'LOADING' && (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
        </Box>
      )}
      {state.kind === 'ERROR' && <Alert type="error">{state.error}</Alert>}
      {state.kind === 'READY' && <CohortHeatmap cohorts={state.cohorts} />}
    </ContentLayout>
  );
}
